import React, { useEffect, useRef, useState } from "react";

import { useToast } from "../context/ToastContext";
import "./ToolPage.css";
import flaskApi from "../services/flaskApi";
import { extractFilenameFromHeader } from "../utils/fileUtils";
import { extractErrorMessage } from "../utils/errorUtils";
import ToolInfoModal from "../components/common/ToolInfoModal";
import ToolFileUploadField from "./shared/ToolFileUploadField";
import ToolPageLayout from "./shared/ToolPageLayout";
import ToolProgressPanel from "./shared/ToolProgressPanel";
import ToolJobsSection from "./shared/ToolJobsSection";
import useJobsPagination from "./shared/useJobsPagination";


const STATUS_DONE = "done";
const STATUS_FAILED = "failed";

// Which input column we match against Shopify. 'sku' is the historical
// default (parent-SKU prefix). 'title' and 'barcode' mirror the per-column
// lookups in the Tag Update tool. Backend form field is `search_by`.
const SEARCH_SKU = "sku";
const SEARCH_TITLE = "title";
const SEARCH_BARCODE = "barcode";

const SEARCH_OPTIONS = [
  { value: SEARCH_SKU, label: "Parent SKU" },
  { value: SEARCH_TITLE, label: "Product Title" },
  { value: SEARCH_BARCODE, label: "Barcode (UPC / EAN / GTIN)" },
];

const SEARCH_HELPER = {
  [SEARCH_SKU]:
    "Matches the parent SKU on Shopify (variant SKUs starting with the value). Accepted headers: Parent SKU, SKU, Variant SKU, Vendor SKU, Style, Style #.",
  [SEARCH_TITLE]:
    "Matches the product title exactly (case-insensitive). Accepted headers: Product Title, Title, Product Name, Name.",
  [SEARCH_BARCODE]:
    "Matches a variant's barcode exactly. Accepted headers: Barcode, Variant Barcode, UPC, EAN, GTIN.",
};

const SEARCH_FILE_HINT = {
  [SEARCH_SKU]: "XLSX or CSV with a Parent SKU column. Optional Price column is echoed into the output.",
  [SEARCH_TITLE]: "XLSX or CSV with a Product Title column. Optional Price column is echoed into the output.",
  [SEARCH_BARCODE]: "XLSX or CSV with a Barcode column. Optional Price column is echoed into the output.",
};

const SEARCH_LABEL = SEARCH_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label;
  return acc;
}, {});

// Output columns. These MUST stay in sync with the backend registry in
// app/services/product_sku_fetcher.py (DEFAULT_COLUMN_LABELS + OPTIONAL_COLUMNS).
// Default columns are always emitted; the value column header depends on the
// search mode ("Parent SKU" / "Title" / "Barcode").
const VALUE_COLUMN_LABEL = {
  [SEARCH_SKU]: "Parent SKU",
  [SEARCH_TITLE]: "Title",
  [SEARCH_BARCODE]: "Barcode",
};

const defaultColumnsFor = (mode) => [
  "Row",
  VALUE_COLUMN_LABEL[mode] || "Parent SKU",
  "Input Price",
  "Status",
  "Vendor",
  "Product Title",
  "Variant SKU",
  "Variant Title",
  "Variant Price",
  "Variant Compare At Price",
  "Message",
];

// Optional, opt-in columns. `key` is sent to the backend `columns` form field
// (comma-separated); `label` is the header the backend emits for it.
const OPTIONAL_COLUMNS = [
  { key: "product_id", label: "Shopify Product ID" },
  { key: "product_status", label: "Product Status" },
  { key: "archived", label: "Archived (Yes/No)" },
  { key: "product_handle", label: "Product Handle" },
  { key: "product_tags", label: "Product Tags" },
  { key: "variant_barcode", label: "Variant Barcode" },
  { key: "cost", label: "Cost per item" },
  { key: "inventory_total", label: "Inventory Available (total)" },
  { key: "inventory_by_location", label: "Inventory by Location" },
];

const OPTIONAL_COLUMN_LABEL = OPTIONAL_COLUMNS.reduce((acc, col) => {
  acc[col.key] = col.label;
  return acc;
}, {});


function FindProductsBySkuTool() {
  const [inputFile, setInputFile] = useState(null);
  const [sheetName, setSheetName] = useState("");
  const [searchBy, setSearchBy] = useState(SEARCH_SKU);
  const [includeArchived, setIncludeArchived] = useState(false);
  // Opt-in optional output columns, keyed by column key -> boolean.
  const [selectedColumns, setSelectedColumns] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloadingJobId, setDownloadingJobId] = useState(null);
  const { addToast } = useToast();
  const [showInfoModal, setShowInfoModal] = useState(false);

  const {
    jobs,
    page,
    totalPages,
    totalFinished,
    setPage,
    refetch: refetchJobs,
  } = useJobsPagination({
    endpoint: "/shopify/find-products-by-sku/jobs",
    cacheKey: "findProductsBySku.jobs",
    pageSize: 10,
  });

  const toolInfo = {
    toolName: "Find Products by SKU",
    description:
      "Upload a list of vendor SKUs, product titles, or barcodes (and optional prices). Pick how to search, and the tool finds every matched Shopify product and variant. Use the output as a working list, or as input to the MAP Policies tool.",
    steps: [
      "Pick what to search by: Parent SKU, Product Title, or Barcode.",
      "Upload an .xlsx (or .csv) with the matching column. A Price column is optional and is echoed into the output.",
      "Under Output Columns, review the always-included columns and tick any extras you want (barcode, cost, inventory, tags, etc.).",
      "Click Start. The job runs server-side; leave the page if you like — your jobs reappear here when you return.",
      "When done, download the report. Every input value with at least one Shopify match emits one row per variant; values with no match emit a single 'not_found' row.",
    ],
    templates: [],
  };

  const guideTips = [
    "Search by Parent SKU (prefix match), Product Title (exact, case-insensitive), or Barcode (exact UPC / EAN / GTIN).",
    "Accepted SKU headers: Parent SKU, SKU, Variant SKU, Vendor SKU, Style, Style #. Title headers: Product Title, Title, Product Name, Name. Barcode headers: Barcode, Variant Barcode, UPC, EAN, GTIN.",
    "Accepted Price column headers: Price, MAP Price, MSRP, Variant Price, New Price. Price is echoed only — never sent to Shopify.",
    "Multi-product matches are intentional. If the same parent SKU is used across color-split products on Shopify (e.g. one product per color), every active product + variant is included.",
    "By default only active products are returned. Tick “Also search archived products” to include archived ones too — draft products are always skipped.",
    "The report always includes the core columns (SKU, title, prices, status, vendor). Under Output Columns, tick extras like Variant Barcode, Cost per item, Inventory Available, Inventory by Location, Product Tags, or Shopify Product ID.",
    "To tell active from archived matches, add the Product Status and/or Archived columns under Output Columns when searching archived products.",
  ];

  const previousStatusRef = useRef({});
  useEffect(() => {
    const previous = previousStatusRef.current;
    jobs.forEach((job) => {
      const prevStatus = previous[job.job_id];
      if (prevStatus && prevStatus !== job.status) {
        if (job.status === STATUS_DONE) {
          addToast(`Job ${job.job_id} complete. Report is ready.`, "success");
        } else if (job.status === STATUS_FAILED) {
          addToast(
            `Job ${job.job_id} failed: ${job.error || "unknown error"}`,
            "error"
          );
        }
      }
    });
    previousStatusRef.current = Object.fromEntries(
      jobs.map((j) => [j.job_id, j.status])
    );
  }, [jobs, addToast]);

  const toggleColumn = (key) => {
    setSelectedColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!inputFile) {
      addToast("Please upload a file.", "error");
      return;
    }
    const maxSize = 100 * 1024 * 1024;
    if (inputFile.size > maxSize) {
      addToast("File is too large. Maximum size is 100MB.", "error");
      return;
    }

    const formData = new FormData();
    formData.append("input_file", inputFile);
    formData.append("search_by", searchBy);
    formData.append("include_archived", includeArchived ? "true" : "false");
    const chosenColumns = OPTIONAL_COLUMNS
      .filter((col) => selectedColumns[col.key])
      .map((col) => col.key);
    if (chosenColumns.length) {
      formData.append("columns", chosenColumns.join(","));
    }
    const trimmedSheet = sheetName.trim();
    if (trimmedSheet) {
      formData.append("sheet_name", trimmedSheet);
    }

    setUploading(true);
    setUploadProgress(0);
    try {
      const response = await flaskApi.post(
        "/shopify/find-products-by-sku",
        formData,
        {
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total || 1)
            );
            setUploadProgress(percentCompleted);
          },
        }
      );

      const jobId = response.data?.job_id;
      if (!jobId) {
        addToast("Server did not return a job ID.", "error");
        return;
      }
      addToast(`Job ${jobId} queued. Tracking progress…`, "info");
      if (page !== 1) await setPage(1);
      else await refetchJobs();
      setInputFile(null);
    } catch (error) {
      console.error(error);
      const message = await extractErrorMessage(error, "Failed to start job.");
      addToast(message, "error");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDownload = async (job) => {
    if (!job?.job_id) return;
    setDownloadingJobId(job.job_id);
    try {
      const response = await flaskApi.get(
        `/shopify/find-products-by-sku/jobs/${job.job_id}/download`,
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition =
        response.headers["content-disposition"] ||
        response.headers["Content-Disposition"];
      const extractedFilename = extractFilenameFromHeader(contentDisposition);
      link.setAttribute(
        "download",
        extractedFilename || job.result_filename || "shopify_products_by_sku.xlsx"
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      addToast("Report downloaded.", "success");
    } catch (error) {
      console.error(error);
      const message = await extractErrorMessage(error, "Failed to download report.");
      addToast(message, "error");
    } finally {
      setDownloadingJobId(null);
    }
  };

  return (
    <>
      <ToolPageLayout
        splitLayout
        title="Find Products by SKU"
        subtitle={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
        tips={guideTips}
        onOpenInfo={() => setShowInfoModal(true)}
      >
        <form onSubmit={handleSubmit} className="tool-form">
          <div className="tool-form-main">
            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Search By</h3>
              </div>
              <div className="tool-field-stack">
                <label className="tool-label" htmlFor="search-by">
                  Match Shopify products via
                </label>
                <select
                  id="search-by"
                  className="tool-input"
                  value={searchBy}
                  onChange={(event) => setSearchBy(event.target.value)}
                  disabled={uploading}
                >
                  {SEARCH_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <span className="tool-helper-text">{SEARCH_HELPER[searchBy]}</span>
              </div>

              <div className="tool-field-stack">
                <div className="shopify-checkbox-row">
                  <label className="shopify-checkbox-label">
                    <input
                      type="checkbox"
                      checked={includeArchived}
                      onChange={(event) => setIncludeArchived(event.target.checked)}
                      disabled={uploading}
                    />
                    Also search archived products
                  </label>
                </div>
                <span className="tool-helper-text">
                  By default only active products are matched. Tick this to include archived products too. To tell them apart in the report, add the Product Status / Archived columns under Output Columns below. Draft products are always skipped.
                </span>
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Input File</h3>
              </div>
              <ToolFileUploadField
                label={`Vendor ${SEARCH_LABEL[searchBy]} list`}
                file={inputFile}
                setFile={setInputFile}
                accept=".xlsx,.xls,.csv"
                helperText={SEARCH_FILE_HINT[searchBy]}
              />
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Tab Name <span className="tool-section-optional">(optional)</span></h3>
              </div>
              <div className="tool-field-stack">
                <label className="tool-label">Excel tab to read</label>
                <p className="tool-helper-text">
                  Only used for .xlsx / .xls files with multiple tabs. Leave blank to use the first tab. Case-sensitive — must match the tab name exactly.
                </p>
                <input
                  type="text"
                  className="tool-input"
                  placeholder="e.g. Vendor List"
                  value={sheetName}
                  onChange={(event) => setSheetName(event.target.value)}
                  disabled={uploading}
                />
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Output Columns</h3>
              </div>
              <div className="tool-field-stack">
                <label className="tool-label">Always included</label>
                <p className="tool-helper-text">
                  These columns are always in the report.
                </p>
                <div className="tool-columns-default">
                  {defaultColumnsFor(searchBy).map((label) => (
                    <span key={label} className="tool-columns-chip">
                      {label}
                    </span>
                  ))}
                </div>
              </div>

              <div className="tool-field-stack">
                <label className="tool-label">Add extra columns <span className="tool-section-optional">(optional)</span></label>
                <p className="tool-helper-text">
                  Tick any of these to include them in the report.
                </p>
                <div className="shopify-checkbox-row">
                  {OPTIONAL_COLUMNS.map((col) => (
                    <label key={col.key} className="shopify-checkbox-label">
                      <input
                        type="checkbox"
                        checked={!!selectedColumns[col.key]}
                        onChange={() => toggleColumn(col.key)}
                        disabled={uploading}
                      />
                      {col.label}
                    </label>
                  ))}
                </div>
              </div>
            </section>

            <div className="tool-action-stack">
              <button type="submit" className="tool-btn-primary" disabled={uploading}>
                {uploading ? "Uploading…" : "Start"}
              </button>

              <ToolProgressPanel
                loading={uploading}
                uploadProgress={uploadProgress}
                uploadMessage="Uploading file…"
                processingMessage="Queueing job…"
              />
            </div>
          </div>
        </form>

        <ToolJobsSection
          title="Your Jobs"
          emptyText="No jobs yet. Submit a file above to start one."
          jobs={jobs}
          page={page}
          totalPages={totalPages}
          totalFinished={totalFinished}
          onPageChange={setPage}
          renderJob={(job) => {
                const progress = job.progress || {};
                const total = Number(progress.total) || 0;
                const processed = Number(progress.processed) || 0;
                const pct =
                  total > 0
                    ? Math.min(100, Math.round((processed / total) * 100))
                    : 0;
                return (
                  <div key={job.job_id} className="tool-job-status">
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Job ID</span>
                      <span className="tool-job-value">{job.job_id}</span>
                    </div>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Input</span>
                      <span className="tool-job-value">{job.input_filename || "—"}</span>
                    </div>
                    {job.search_by ? (
                      <div className="tool-job-status-row">
                        <span className="tool-job-label">Search by</span>
                        <span className="tool-job-value">
                          {SEARCH_LABEL[job.search_by] || job.search_by}
                        </span>
                      </div>
                    ) : null}
                    {job.include_archived ? (
                      <div className="tool-job-status-row">
                        <span className="tool-job-label">Archived</span>
                        <span className="tool-job-value">Included</span>
                      </div>
                    ) : null}
                    {Array.isArray(job.columns) && job.columns.length ? (
                      <div className="tool-job-status-row">
                        <span className="tool-job-label">Extra columns</span>
                        <span className="tool-job-value">
                          {job.columns
                            .map((key) => OPTIONAL_COLUMN_LABEL[key] || key)
                            .join(", ")}
                        </span>
                      </div>
                    ) : null}
                    {job.sheet_name ? (
                      <div className="tool-job-status-row">
                        <span className="tool-job-label">Tab</span>
                        <span className="tool-job-value">{job.sheet_name}</span>
                      </div>
                    ) : null}
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Status</span>
                      <span className={`tool-job-value tool-job-status-${job.status}`}>
                        {job.status}
                      </span>
                    </div>
                    {total > 0 ? (
                      <>
                        <div className="tool-job-status-row">
                          <span className="tool-job-label">Progress</span>
                          <span className="tool-job-value">
                            {processed.toLocaleString()} / {total.toLocaleString()} ({pct}%)
                          </span>
                        </div>
                        <div className="progress-bar-container">
                          <div
                            className="progress-bar-fill"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </>
                    ) : null}
                    <div className="tool-job-counts">
                      <span>Matched: {progress.matched || 0}</span>
                      <span>Not found: {progress.not_found || 0}</span>
                      <span>Errors: {progress.errors || 0}</span>
                      <span>Skipped: {progress.skipped || 0}</span>
                      <span>Variants: {progress.variants_emitted || 0}</span>
                      {job.include_archived ? (
                        <span>Archived: {progress.archived_variants || 0}</span>
                      ) : null}
                    </div>
                    {job.status === STATUS_FAILED && job.error ? (
                      <div className="tool-job-error">Error: {job.error}</div>
                    ) : null}
                    {job.status === STATUS_DONE ? (
                      <button
                        type="button"
                        className="tool-btn-primary"
                        onClick={() => handleDownload(job)}
                        disabled={downloadingJobId === job.job_id}
                      >
                        {downloadingJobId === job.job_id ? "Downloading…" : "Download Report"}
                      </button>
                    ) : null}
                  </div>
                );
              }}
            />
      </ToolPageLayout>

      <ToolInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        toolName={toolInfo.toolName}
        description={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
      />
    </>
  );
}


export default FindProductsBySkuTool;
